/**
 * ProjectBadgeSubscriber — pushes "my projects" and "overdue milestones"
 * via SignalR.
 *
 * Phase 4 of the badge migration. Deltas come from
 * ProjectCreatedBadgeConsumer (per-user via Clients.User) and
 * ProjectMilestoneCompletedBadgeConsumer (broadcast).
 */

import { useCallback, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import {
  useProjectService,
  useProjectMilestoneService,
  useDomainDashboard,
  type BadgeDeltaMessage,
  type BadgeSnapshotMessage,
} from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { useProjectBadgesStore } from './projectBadgesStore';

const PROJECT_ROLES = ['Projects Access', 'Admin'];
const DEBUG = import.meta.env.DEV;
const log = (...args: any[]) => {
  if (DEBUG) console.log('[ProjectBadgeSubscriber]', ...args); // eslint-disable-line no-console
};

interface ProjectDeltas {
  myProjects?: number;
  overdueMilestones?: number;
}

const isMilestoneDone = (m: any): boolean => {
  if (m?.completedDateTime) return true;
  const name = (m?.statusName ?? '').toLowerCase();
  return name === 'done' || name === 'completed' || name === 'closed';
};

export function ProjectBadgeSubscriber() {
  const { hasAnyRole } = useEnrichedAuth();
  const enabled = hasAnyRole(PROJECT_ROLES);

  const projectService = useProjectService();
  const milestoneService = useProjectMilestoneService();
  const setFromMetrics = useProjectBadgesStore((s) => s.setFromMetrics);
  const applyDelta = useProjectBadgesStore((s) => s.applyDelta);
  const reset = useProjectBadgesStore((s) => s.reset);

  const refetchBaseline = useCallback(async () => {
    try {
      const [my, milestones] = await Promise.all([
        projectService.getMyListView() as Promise<any[]>,
        milestoneService.getAll() as Promise<any[]>,
      ]);
      const today = dayjs().startOf('day');
      const myProjects = (my ?? []).length;
      const overdueMilestones = (milestones ?? []).filter((m: any) => {
        if (!m?.dueDate) return false;
        if (isMilestoneDone(m)) return false;
        return dayjs(m.dueDate).isBefore(today);
      }).length;
      log('baseline', { myProjects, overdueMilestones });
      setFromMetrics({ myProjects, overdueMilestones });
    } catch (err) {
      log('baseline fetch failed', err);
    }
  }, [projectService, milestoneService, setFromMetrics]);

  useEffect(() => {
    if (!enabled) return;
    refetchBaseline();
  }, [enabled, refetchBaseline]);

  const { isConnected, requestBadgeSnapshot } = useDomainDashboard('projects', {
    autoConnect: enabled,
    autoSubscribe: false,
    onBadgeSnapshot: (msg: BadgeSnapshotMessage<ProjectDeltas>) => {
      if (msg.domain !== 'Projects') return;
      log('snapshot', msg.snapshot);
      setFromMetrics(msg.snapshot ?? {});
    },
    onBadgeDelta: (msg: BadgeDeltaMessage<ProjectDeltas>) => {
      if (msg.domain !== 'projects') return;
      log('delta', msg.reason, msg.deltas);
      applyDelta(msg.deltas ?? {});
    },
  });

  const wasConnectedRef = useRef(false);
  useEffect(() => {
    if (!enabled) return;
    if (isConnected && !wasConnectedRef.current) {
      wasConnectedRef.current = true;
      return;
    }
    if (isConnected && wasConnectedRef.current) {
      log('reconnected → refetch baseline');
      refetchBaseline();
      void requestBadgeSnapshot();
    }
  }, [enabled, isConnected, refetchBaseline, requestBadgeSnapshot]);

  useEffect(() => () => reset(), [reset]);

  useEffect(() => {
    log('mounted', { enabled });
  }, [enabled]);

  return null;
}
